import type { Doc } from "building-types/doc";
import type { OptionChoices, InitializedOptionChoices } from "building-types/options/optionChoices";
import { applyRulesToOptionChoices } from "doc/options/applyRulesToOptionChoices";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { getOptionChoicesWithDefaults, sanitizeDocOptionChoices } from "doc/options/optionChoicesTools";

/**
 * optionChoicesNew is a constructor function that initializes option choices. If persistedOptionChoices
 * are defined they are generally respected, even if they are empty; we assume that any saved option choices
 * have already had side effects like option rules applied to them. If persistedOptionChoices are undefined,
 * then side effects like option rules are applied.
 * @param doc the doc object
 * @param persistedOptionChoices if defined these will be returned without being mutated, undefined here
 * implies that default options from the doc will be used to evaluate option rules
 * @param controllingChoices if defined these will be used to mutate default option choices
 * according to option rules; if not defined, all default choices will be evauated against option rules
 * @param optionStateV2 if true, skip applying rules (they'll be applied in getGenerateBuildingParams)
 */

export function optionChoicesNew(
  doc: Doc,
  persistedOptionChoices?: OptionChoices,
  controllingChoices?: OptionChoices,
  optionStateV2: boolean = false,
): InitializedOptionChoices {
  if (optionStateV2) {
    return { optionChoices: { ...persistedOptionChoices, ...controllingChoices } };
  }

  // if option choices exist (even if they are empty), we don't need to apply rules
  if (!!persistedOptionChoices) {
    return { optionChoices: sanitizeDocOptionChoices(doc, persistedOptionChoices) };
  }

  // apply controlling choices to defaults
  const defaultChoices = { ...getOptionChoicesWithDefaults(docToOptionDefinitions(doc)), ...controllingChoices };

  const optionChoicesWithRules = applyRulesToOptionChoices(doc, defaultChoices, controllingChoices ?? defaultChoices);

  return { optionChoices: sanitizeDocOptionChoices(doc, optionChoicesWithRules) };
}
